import { useState, useRef } from 'react'
import Layout from '../../components/Layout'
import api from '../../services/api'
import Toast from '../../utils/toast'
import loaderGif from '../../assets/loader.gif'
import './style.css'

// YYYY-MM-DD
const toDateString = (d) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Default range: last 7 days
const defaultRange = () => {
  const today = new Date()
  const weekAgo = new Date()
  weekAgo.setDate(today.getDate() - 7)
  return [toDateString(weekAgo), toDateString(today)]
}

/**
 * GameReport Component
 *
 * Reference layout (user, 2026-09-28): From / To / Type (All, Match, Fancy) +
 * Game List, then a game dropdown + Show Game Report / Master Game Report.
 * Table is Sr.No / Name / Amount. Game List fills the dropdown from
 * /admin/reports/game-list; the two report buttons hit /admin/reports/game-report
 * (master=false → per client, master=true → per direct downline).
 */
function GameReport() {
  const [initialRange] = useState(defaultRange)
  const [fromDate, setFromDate] = useState(initialRange[0])
  const [toDate, setToDate] = useState(initialRange[1])
  const [type, setType] = useState('all')
  const [games, setGames] = useState([])
  const [eventid, setEventid] = useState('All')
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)
  const fromDateInputRef = useRef(null)
  const toDateInputRef = useRef(null)

  const handleCalendarIconClick = (inputRef) => {
    if (inputRef && inputRef.current) {
      inputRef.current.showPicker?.() || inputRef.current.click()
    }
  }

  const showError = (err) => {
    const msg = err?.response?.data?.message || err?.response?.data?.error
    if (msg) Toast.fire({ icon: 'error', title: msg })
  }

  const handleGameList = async () => {
    setLoading(true)
    try {
      const response = await api.post('/admin/reports/game-list', { fromDate, toDate, type })
      setGames(response.data?.success ? response.data.data : [])
      setEventid('All')
    } catch (err) {
      console.error('Error loading game list:', err)
      setGames([])
      showError(err)
    } finally {
      setLoading(false)
    }
  }

  const loadReport = async (master) => {
    setLoading(true)
    try {
      const response = await api.post('/admin/reports/game-report', { fromDate, toDate, type, eventid, master })
      setTableData(response.data?.success ? response.data.data : [])
    } catch (err) {
      console.error('Error loading game report:', err)
      setTableData([])
      showError(err)
    } finally {
      setLoading(false)
    }
  }

  const renderDate = (label, value, setValue, ref) => (
    <div className="col-md-2">
      <label>{label}</label>
      <div className="mx-datepicker">
        <div className="mx-input-wrapper">
          <input
            ref={ref}
            type="date"
            autoComplete="off"
            className="mx-input form-control"
            value={value}
            max={toDateString(new Date())}
            onChange={(e) => setValue(e.target.value)}
          />
          <i
            className="mx-icon-calendar"
            onClick={() => handleCalendarIconClick(ref)}
            style={{ cursor: 'pointer', pointerEvents: 'auto' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="1em" height="1em">
              <path d="M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z"></path>
            </svg>
          </i>
        </div>
      </div>
    </div>
  )

  return (
    <Layout title="Game Report - Diamond Admin">
      {loading && (
        <div style={{
          display: 'block', position: 'fixed', zIndex: 9999,
          backgroundImage: `url(${loaderGif})`, backgroundColor: '#666', opacity: 0.4,
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center',
          left: 0, bottom: 0, right: 0, top: 0, backgroundSize: '100px 100px',
        }} />
      )}
      <div className="listing-grid">
        <div className="account-list-statement">
          {/* Page Title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-flex align-items-center justify-content-between">
                <h4 className="mb-0 font-size-18">Game Report</h4>
              </div>
            </div>
          </div>

          {/* Filter Form */}
          <div className="row">
            <div className="col-12">
              <form method="post" className="ajaxFormSubmit" onSubmit={(e) => e.preventDefault()}>
                <div className="row row5">
                  {renderDate('From', fromDate, setFromDate, fromDateInputRef)}
                  {renderDate('To', toDate, setToDate, toDateInputRef)}
                  <div className="col-md-2">
                    <div className="form-group">
                      <label>Type</label>
                      <select className="form-select" value={type} onChange={(e) => setType(e.target.value)}>
                        <option value="all">All</option>
                        <option value="match">Match</option>
                        <option value="fancy">Fancy</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <div className="form-group">
                      <button type="button" className="btn btn-primary" onClick={handleGameList}>Game List</button>
                    </div>
                  </div>
                </div>
                <div className="row row5 mt-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <select className="form-select" value={eventid} onChange={(e) => setEventid(e.target.value)}>
                        <option value="All">All</option>
                        {games.map((g) => (
                          <option key={g.eventid} value={g.eventid}>{g.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <button type="button" className="btn btn-primary" onClick={() => loadReport(false)}>Show Game Report</button>
                      {' '}
                      <button type="button" className="btn btn-primary" onClick={() => loadReport(true)}>Master Game Report</button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Table Section */}
          <div className="row">
            <div className="col-12">
              <div className="table-responsive mb-0 mt-3">
                <table
                  id="gameReportTbl"
                  role="table"
                  aria-busy={loading}
                  aria-colcount="3"
                  className="table b-table table-striped table-bordered"
                >
                  <thead role="rowgroup">
                    <tr role="row">
                      <th role="columnheader" scope="col" className="position-relative"><div>Sr.No</div></th>
                      <th role="columnheader" scope="col" className="position-relative"><div>Name</div></th>
                      <th role="columnheader" scope="col" className="position-relative"><div>Amount</div></th>
                    </tr>
                  </thead>
                  <tbody role="rowgroup">
                    {tableData.length === 0 ? (
                      <tr role="row" className="b-table-empty-row">
                        <td colSpan="3" role="cell"><div className="text-center my-2">No data available in table</div></td>
                      </tr>
                    ) : (
                      tableData.map((r, idx) => (
                        <tr key={r.name} role="row">
                          <td role="cell">{idx + 1}</td>
                          <td role="cell">{r.name}</td>
                          <td role="cell" className={r.amount < 0 ? 'text-danger' : 'text-success'}>{r.amount}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default GameReport
